import { Billboard } from '@react-three/drei'
import React, { useRef, useMemo } from 'react'
import * as THREE from 'three'

export default function Crosshair3D({ position = [0,0,0], size = 0.5, color = '#00ff00' }) {
  // X-shaped crosshair using InstancedMesh for optimal batching performance
  const lineWidth = size * 0.1;
  const lineLength = size * 0.8;
  
  // Create geometries for glow and main lines
  const glowGeometry = useMemo(() => 
    new THREE.BoxGeometry(lineLength, lineWidth, 0.08), 
    [lineLength, lineWidth]
  );
  
  const mainGeometry = useMemo(() => 
    new THREE.BoxGeometry(lineLength, lineWidth, 0.05), 
    [lineLength, lineWidth]
  );
  
  // Create materials
  const glowMaterial = useMemo(() => 
    new THREE.MeshBasicMaterial({ 
      color, 
      opacity: 0.4, 
      transparent: true, 
      depthTest: false, 
      depthWrite: false 
    }), 
    [color]
  );
  
  const mainMaterial = useMemo(() => 
    new THREE.MeshBasicMaterial({ 
      color, 
      depthTest: false, 
      depthWrite: false 
    }), 
    [color]
  );
  
  const glowMeshRef = useRef();
  const mainMeshRef = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  
  // Set up instance matrices for the X shape
  React.useEffect(() => {
    if (glowMeshRef.current && mainMeshRef.current) {
      // Glow instances: two diagonals
      dummy.rotation.set(0, 0, Math.PI / 4);
      dummy.updateMatrix();
      glowMeshRef.current.setMatrixAt(0, dummy.matrix);
      
      dummy.rotation.set(0, 0, -Math.PI / 4);
      dummy.updateMatrix();
      glowMeshRef.current.setMatrixAt(1, dummy.matrix);
      
      // Main instances: two diagonals
      dummy.rotation.set(0, 0, Math.PI / 4);
      dummy.updateMatrix();
      mainMeshRef.current.setMatrixAt(0, dummy.matrix);
      
      dummy.rotation.set(0, 0, -Math.PI / 4);
      dummy.updateMatrix();
      mainMeshRef.current.setMatrixAt(1, dummy.matrix);
      
      glowMeshRef.current.instanceMatrix.needsUpdate = true;
      mainMeshRef.current.instanceMatrix.needsUpdate = true;
    }
  }, [dummy]);
  
  return (
    <Billboard position={position}>
      {/* Glow effect: instanced mesh */}
      <instancedMesh
        ref={glowMeshRef}
        args={[glowGeometry, glowMaterial, 2]}
        renderOrder={9998}
        frustumCulled={false}
      />
      
      {/* Main crosshair: instanced mesh */}
      <instancedMesh
        ref={mainMeshRef}
        args={[mainGeometry, mainMaterial, 2]}
        renderOrder={9999}
        frustumCulled={false}
      />
    </Billboard>
  )
}
